import type { Node as CollectionNode } from '@react-types/shared';
import type { AriaPopoverProps, AriaTagGroupProps, AriaTagProps } from 'react-aria';
import * as React from 'react';
import { useFocus, useFocusRing, useTag, useTagGroup } from 'react-aria';
import { Popover } from 'react-aria-components';
import { mergeRefs } from 'react-merge-refs';
import {
  type ItemProps,
  type ListState,
  useListState,
  useOverlayTriggerState,
} from 'react-stately';
import useMeasure from 'react-use-measure';

import type { IconName } from '../../assets/Icon/Icon';
import type { FromMultipleSelectionProps } from '../../common/props';
import { Icon } from '../../assets/Icon/Icon';
import { toMultipleSelectionProps } from '../../common/props';
import { selectors, transitions } from '../../controls/shared/styles';
import { colors, darkThemeSelector, styled } from '../../stitches.config';
import { Body } from '../../text/Body';
import { Badge } from '../Badge/Badge';
import { BaseInputContainer } from '../BaseInput/BaseInput';
import { MultiComboBoxOverlay } from './MultiComboxBoxOverlay';

export const MultiComboBoxTagsList = styled('div', {
  cursor: 'text',
  width: '100%',
  '&:focus-visible': {
    outline: 'none',
  },
  variants: {
    size: {
      large: {
        paddingY: '$8',
        paddingX: '$12',
      },
      medium: {
        paddingY: '$4',
        paddingX: '$8',
      },
      small: {
        paddingY: '$4',
        paddingX: '$6',
      },
    },
  },
});
MultiComboBoxTagsList.displayName = 'TagsList';

const TagsListInner = styled('div', {
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '$4',
  minHeight: '$20', // this is the height of the "small" badge size
  outline: 'none',
});
TagsListInner.displayName = 'TagsListInner';

const BadgeContainer = styled('div', {
  cursor: 'pointer',
  '&[data-focus-visible=true]': {
    borderRadius: '$8',
    boxShadow: '0 0 0 2px $colors$blue500',
    outline: 'none',
  },
});
BadgeContainer.displayName = 'BadgeContainer';

const MultiComboBoxBadge = styled(Badge, {
  verticalAlign: 'top',

  variants: {
    size: {
      'x-small': {
        '@notDesktop': {
          heightAll: 'unset',
        },

        '@desktop': {
          heightAll: 'unset',
        },
      },
      small: {
        '@notDesktop': {
          heightAll: 'unset',
        },

        '@desktop': {
          heightAll: 'unset',
        },
      },
      medium: {
        '@notDesktop': {
          heightAll: 'unset',
        },

        '@desktop': {
          heightAll: 'unset',
        },
      },
      large: {
        '@notDesktop': {
          heightAll: 'unset',
        },

        '@desktop': {
          heightAll: 'unset',
        },
      },
    },
  },
});
MultiComboBoxBadge.displayName = 'MultiComboBoxBadge';

const Placeholder = styled(Body, {
  display: 'block',
  color: '$$placeholderColor',
});
Placeholder.displayName = 'Placeholder';

const MultiComboBoxAdd = styled(Icon, {
  color: colors.iconNeutralLight,
  cursor: 'pointer',
  transition: transitions.control,

  [darkThemeSelector]: {
    color: colors.iconNeutralDark,
  },

  [selectors.hover]: {
    color: colors.bodyNeutralLight,

    [darkThemeSelector]: {
      color: colors.bodyNeutralDark,
    },
  },
});
MultiComboBoxAdd.displayName = 'MultiComboBoxAdd';

const MultiComboBoxIcon = styled(Icon, {
  width: '$14',
  height: '$14',
  color: colors.iconNeutralLight,
  cursor: 'pointer',
  transition: transitions.control,

  [darkThemeSelector]: {
    color: colors.iconNeutralDark,
  },

  [selectors.hover]: {
    color: colors.bodyNeutralLight,

    [darkThemeSelector]: {
      color: colors.bodyNeutralDark,
    },
  },
});
MultiComboBoxIcon.displayName = 'MultiComboBoxIcon';

const MultiComboBoxContainer = styled(BaseInputContainer, {
  alignItems: 'center',

  variants: {
    icon: {
      true: {
        paddingLeft: '$8',
      },
    },
  },
});
MultiComboBoxContainer.displayName = 'MultiComboBoxContainer';

export const MultiComboBoxPopover = styled(Popover, {
  padding: '$4',
  overflow: 'auto',
  backgroundColor: colors.bgApplicationLight,
  borderRadius: '$8',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',

  // TODO(radix-vs-react-aria): this is necessary because we currently open this component, which
  // composes React Aria's `Popover` within a Radix `DropdownMenu` for the `MultiComboBox` filter.
  // Radix applies `pointer-events: none` to the `<body>` element when display modal content and
  // applies `pointer-events: auto` to the content itself. Because the React Aria `Popover` uses a
  // portal, it doesn't inherit the `pointer-events` style and therefore is not interactive.
  //
  // We should keep an eye on this and consider a more global solution so that any React Aria
  // `Portal` or modal components have `pointer-events: auto` applied as long as we're still using
  // both libraries.
  //
  // See:
  //   - https://github.com/radix-ui/primitives/issues/2122
  //   - packages/common/src/components/AutoCollection/TableFilters/TableFiltersToken.tsx
  //   - apps/dashboard/src/components/Table/createMultiComboBoxFilter.tsx
  pointerEvents: 'auto',

  [darkThemeSelector]: {
    backgroundColor: colors.bgApplicationDark,
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
  },
});
MultiComboBoxPopover.displayName = 'MultiComboBoxPopover';

type MultiComboBoxSize = 'small' | 'medium' | 'large';

interface TagProps<T> extends AriaTagProps<T> {
  state: ListState<T>;
  isDisabled?: boolean;
}

type MultiComboBoxItemNodeProps = {
  tooltipContents?: React.ReactNode;
};

function Tag<T>(props: TagProps<T>) {
  const { item, state } = props;
  const itemTooltipContents = (item.props as MultiComboBoxItemNodeProps | null)?.tooltipContents;
  const ref = React.useRef<HTMLDivElement>(null);
  const { focusProps, isFocusVisible } = useFocusRing({ within: true });
  const { rowProps, gridCellProps } = useTag(props, state, ref);

  const onKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (props.isDisabled) return;

      if (e.key === 'Backspace') {
        state.selectionManager.toggleSelection(item.key);
        e.stopPropagation();
        return;
      }

      rowProps.onKeyDown?.(e);
    },
    [props.isDisabled, state, item.key, rowProps],
  );

  const handleClick = React.useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      rowProps.onClick?.(e);
      e.stopPropagation();
      e.nativeEvent.stopImmediatePropagation();
    },
    [rowProps],
  );

  const handleMouseDown = React.useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
  }, []);

  return (
    <BadgeContainer
      {...(props.isDisabled
        ? {}
        : { ...rowProps, onClick: handleClick, onMouseDown: handleMouseDown })}
      {...(props.isDisabled ? {} : focusProps)}
      ref={ref}
      onKeyDown={onKeyDown}
      data-focus-visible={isFocusVisible}
      data-multicombobox-tag
    >
      <MultiComboBoxBadge
        {...gridCellProps}
        icon="cross"
        variant="brand"
        size="small"
        tooltipContents={itemTooltipContents}
      >
        {item.rendered}
      </MultiComboBoxBadge>
    </BadgeContainer>
  );
}

export function useCloseOnFocusOutside(
  overlayState: ReturnType<typeof useOverlayTriggerState>,
  triggerRef: React.RefObject<HTMLButtonElement | null>,
  overlayRef: React.RefObject<HTMLDivElement | null>,
) {
  React.useEffect(() => {
    const handleFocus = (e: FocusEvent) => {
      if (!overlayState.isOpen) return;

      if (
        !triggerRef.current?.contains(e.target as Node) &&
        !overlayRef.current?.contains(e.target as Node)
      ) {
        overlayState.close();
      }
    };

    document.addEventListener('focusin', handleFocus);

    return () => {
      document.removeEventListener('focusin', handleFocus);
    };
  }, [overlayRef, overlayState, triggerRef]);
}

interface MultiComboBoxProps<T> extends FromMultipleSelectionProps<AriaTagGroupProps<T>> {
  actions?: React.ReactNode;
  /**
   * The list of options to render inside the popover.
   * - Use `<MultiComboBoxItem>` to render an option.
   * - Use `<MultiComboBoxSection>` to group options into sections.
   */
  children: AriaTagGroupProps<T>['children'];
  /**
   * Whether the input is disabled.
   * @default false
   */
  disabled?: boolean;
  /**
   * The icon to show in the input.
   */
  icon?: IconName;
  /**
   * The placeholder text to show when there are no selected items.
   * @default 'Select items'
   */
  placeholder?: string;
  /**
   * The placement of the popover relative to the input.
   * @default 'bottom start'
   */
  placement?: AriaPopoverProps['placement'];
  /**
   * Whether to show the "add" button in the input.
   * @default false
   */
  showAdd?: boolean;
  /**
   * The size of the input.
   * @default 'medium'
   */
  size?: MultiComboBoxSize;
  /**
   * The width of the popover.
   */
  width?: string | number;
  /**
   * Whether the input is in an indeterminate state.
   */
  indeterminate?: boolean;
  renderSelected?: (selectedItems: CollectionNode<T>[]) => React.ReactElement;
  enableSelectAll?: boolean;
  className?: string;
  onKeyDown?: React.KeyboardEventHandler;
}

const MultiComboBoxInnerContainer = styled('div', {
  width: '100%',
});
MultiComboBoxInnerContainer.displayName = 'MultiComboBoxInnerContainer';

function MultiComboBoxInner<T extends object>(
  props: MultiComboBoxProps<T>,
  ref: React.ForwardedRef<HTMLDivElement>,
) {
  const renamedProps = toMultipleSelectionProps(props);

  const {
    icon,
    placement = 'bottom start',
    showAdd,
    size = 'medium',
    placeholder = 'Select items',
    indeterminate,
    enableSelectAll = true,
    className,
    onKeyDown,
  } = renamedProps;

  const [measureRef, { width: inputWidth }] = useMeasure();
  const [isFocused, setIsFocused] = React.useState(false);
  const { focusProps } = useFocus({ onFocusChange: (value) => setIsFocused(value) });

  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const pointerDownTargetRef = React.useRef<EventTarget | null>(null);

  const tagGroupState = useListState({
    ...renamedProps,
    selectionMode: 'multiple',
    selectionBehavior: 'toggle',
  });

  const overlayState = useOverlayTriggerState({});

  const { gridProps } = useTagGroup(renamedProps, tagGroupState, triggerRef);

  const selectedItems = [...tagGroupState.collection.getKeys()]
    .filter((key) => tagGroupState.selectionManager.isSelected(key))
    .map((key) => tagGroupState.collection.getItem(key))
    .filter((item) => !!item) as CollectionNode<T>[];

  const openPopoverAndFocusInput = () => {
    if (!overlayState.isOpen) {
      overlayState.open();
    }
  };

  const handleContainerPointerDownCapture = React.useCallback((e: React.PointerEvent) => {
    pointerDownTargetRef.current = e.target;
  }, []);

  const handleContainerClick = (e: React.MouseEvent) => {
    const clickTarget = e.target as Element;
    const pointerDownTarget = pointerDownTargetRef.current as Element | null;
    pointerDownTargetRef.current = null;

    const isTagClick =
      clickTarget?.closest?.('[data-multicombobox-tag]') ||
      pointerDownTarget?.closest?.('[data-multicombobox-tag]');
    if (isTagClick) {
      return;
    }
    openPopoverAndFocusInput();
  };

  return (
    <MultiComboBoxInnerContainer ref={ref} className={className}>
      <MultiComboBoxContainer
        as="div"
        {...focusProps}
        icon={!!icon}
        size={size}
        onPointerDownCapture={handleContainerPointerDownCapture}
        onClick={handleContainerClick}
        ref={mergeRefs([triggerRef, measureRef])}
        isDisabled={renamedProps.isDisabled}
        data-is-focused={isFocused || overlayState.isOpen ? 'true' : undefined}
        tabIndex={renamedProps.isDisabled ? undefined : 0}
        onKeyDown={(e) => {
          if (renamedProps.isDisabled) return;

          if (onKeyDown) {
            onKeyDown(e);
          }

          if (e.key === 'Backspace') {
            tagGroupState.selectionManager.clearSelection();
          }

          if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
            openPopoverAndFocusInput();
          }
        }}
      >
        {icon && <MultiComboBoxIcon icon={icon} size={size === 'small' ? 12 : 16} />}
        {/* multi-select-dropdown is added for detection while doing autofocus */}
        <MultiComboBoxTagsList size={size} data-select-dropdown="true">
          {/* eslint-disable-next-line no-nested-ternary */}
          {props?.renderSelected ? (
            props.renderSelected(selectedItems)
          ) : // eslint-disable-next-line no-nested-ternary
          indeterminate ? (
            <>&nbsp;&ndash;</>
          ) : selectedItems.length ? (
            <TagsListInner
              {...gridProps}
              onKeyDownCapture={(e) => {
                if (renamedProps.isDisabled) return;

                // override useListState behavior of clearing the value when pressing Escape
                if (e.key === 'Escape') {
                  e.stopPropagation();
                  return;
                }

                gridProps.onKeyDownCapture?.(e);
              }}
            >
              {selectedItems.map((item) => (
                <Tag
                  key={item.key}
                  item={item}
                  state={tagGroupState}
                  isDisabled={renamedProps.isDisabled}
                />
              ))}
              {showAdd && <MultiComboBoxAdd icon="plus" size={size === 'small' ? 10 : 14} />}
            </TagsListInner>
          ) : (
            <Placeholder>{placeholder}</Placeholder>
          )}
        </MultiComboBoxTagsList>
      </MultiComboBoxContainer>
      <MultiComboBoxPopover
        triggerRef={triggerRef}
        isOpen={overlayState.isOpen}
        onOpenChange={overlayState.setOpen}
        placement={placement}
        offset={4}
        style={{
          width: renamedProps.width,
          minWidth: inputWidth,
          maxWidth: renamedProps.width === '100%' ? inputWidth : '400px',
        }}
      >
        <MultiComboBoxOverlay
          {...renamedProps}
          onSelectionChange={(e) => {
            if (e === 'all' && enableSelectAll) {
              tagGroupState.selectionManager.toggleSelectAll();
              tagGroupState.selectionManager.setSelectedKeys(tagGroupState.collection.getKeys());
            } else {
              tagGroupState.selectionManager.setSelectedKeys(e);
            }
          }}
          isOpen={overlayState.isOpen}
          onClose={overlayState.close}
          selectedKeys={tagGroupState.selectionManager.selectedKeys}
          disabledKeys={renamedProps.disabledKeys}
          enableSelectAll={enableSelectAll}
        />
      </MultiComboBoxPopover>
    </MultiComboBoxInnerContainer>
  );
}

export const MultiComboBox = React.forwardRef(MultiComboBoxInner) as <T extends object>(
  props: MultiComboBoxProps<T> & { ref?: React.ForwardedRef<HTMLUListElement> },
) => ReturnType<typeof MultiComboBoxInner>;

export type MultiComboBoxItemProps<T extends object> = ItemProps<T> & {
  tooltipContents?: React.ReactNode;
};

export { Item as MultiComboBoxItem, Section as MultiComboBoxSection } from 'react-stately';
export type { MultiComboBoxProps };
